import { Box, Button, Checkbox, FormControl } from '@primer/react';
import { useSubmitRequestDemoForm } from '@upright/ui/api/activities/use-submit-request-demo-form';
import { LeadFormContactInfo } from '@upright/ui/common/activities/lead-form/types';
import { ExternalLink } from '@upright/ui/common/generic/components/bou-tool-header/mobile-header/shared-menus';
import { getNiceButtonStyle } from '@upright/ui/common/generic/components/buttons/nice-button';
import { BouTextAreaInput } from '@upright/ui/common/generic/components/inputs/bou-text-area-input';
import { BouTextInputFacade } from '@upright/ui/common/generic/components/inputs/bou-text-input-facade';
import { DropdownInput } from '@upright/ui/common/generic/components/inputs/dropdown-input/dropdown-input';
import { getTitleStyle } from '@upright/ui/common/generic/content/texts/bou-title/title-style';
import {
  FormModalBottomBar,
  FormModalContent,
  FormModalTopBar,
} from '@upright/ui/common/generic/modals/form-modal/form-modal-parts';
import { validate } from 'email-validator';
import { compact, isEqual } from 'lodash';
import { ReactNode, SetStateAction, Dispatch, FunctionComponent, MouseEvent, useEffect, useState } from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';
import styled, { css } from 'styled-components';
import { RequestDemoFormId, LeadFormRequest } from '../../../../common/activities/contact-forms/request-demo/types';
import { usePushAnonymousGtmEvent } from '../../generic/components/google-tag-manager/use-push-gtm-event';
import {
  CustomGtmEvent,
  EmailEventPayload,
  EmailGtmEvent,
} from '../../generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { SuccessCheckmark } from '../../generic/components/success-check-mark';
import { getTextStyle } from '../../generic/content/texts/bou-text/text-style';
import { BouColor } from '../../generic/global/bou-colors';
// eslint-disable-next-line upright/no-platform-color
import { PlatformColor } from '../../generic/global/platform-colors';
import { mobileOrNarrower, mobileOrWider, tabletOrWider } from '../../generic/layout/breakpoint-utils';
import { Link } from '../../generic/components/primer-custom/link';
import { StyledLabel } from '../../generic/components/inputs/bou-text-input-facade/index';
import { isFreeEmailDomain } from '@upright/common/iam/users/email-domains-to-block';
import { isTrashEmailDomain } from '@upright/common/iam/users/email-domains-to-block';

interface Props {
  title: string;
  formId: RequestDemoFormId | string;
  hideMessage: boolean | undefined;
  hideTitle: boolean | undefined;
  submitText: string;
  textBelowTitle: ReactNode | undefined;
  contactInfo: LeadFormContactInfo | undefined;
  submitEvent: EmailGtmEvent;
  interactionEvent: CustomGtmEvent;
  inModal: boolean;
  askConsent: boolean;
  successTitle: string;
  successMessage: string;
  showOrganizationTypeField?: boolean;
  requirePhoneNumber?: boolean;
  CustomInputComponent?: FunctionComponent<{ setCustomFieldValue: Dispatch<SetStateAction<string>> }>;
}

// Options for webinar_registrant_interest field in HubSpot
// Note: This field is poorly named but kept as "webinar_registrant_interest" for HubSpot integration compatibility
const webinarInterestOptions = [
  'Enterprise',
  'SME',
  'Consultant',
  'Asset manager/owner',
  'PE/VC investor',
  'Regulator or other authority',
  'Academia',
  'Other',
] as const;

export const LeadForm = (props: Props) => {
  const initialState: LeadFormRequest = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    // Note: Field name kept as \"webinar_registrant_interest\" for HubSpot integration compatibility
    webinar_registrant_interest: undefined,
    agreedToComms: undefined,
    formId: props.formId,
  };
  const inModal = props.inModal;

  const showOrganizationTypeField = props.showOrganizationTypeField === true;
  const requirePhoneNumber = props.requirePhoneNumber !== false; // defaults to true
  const [formInfo, setFormInfo] = useState<LeadFormRequest>(initialState);
  const [submitButtonClicked, setSubmitButtonClicked] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { setIdentity } = useTrackingCode();
  const [interacted, setInteracted] = useState(false);
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [emailClicked, setEmailClicked] = useState(false);
  const [customInputMessage, setCustomInputMessage] = useState(''); // Gets appended to the message field when submitted

  const pushGtmEvent = usePushAnonymousGtmEvent();

  function onSuccess() {
    setSubmitted(true);
    setSubmitButtonClicked(false);
  }

  const submitQuery = useSubmitRequestDemoForm(onSuccess);

  function submitForm(event: MouseEvent) {
    event.preventDefault();
    setSubmitButtonClicked(true);
    const payload: EmailEventPayload = {
      event: props.submitEvent,
      data: {
        email: formInfo.email,
      },
    };
    pushGtmEvent(payload);
    try {
      setIdentity(formInfo.email);
    } catch {
      console.error(`HubSpot setIdentify failed for email ${formInfo.email}`);
    }
    if (checkValues(formInfo)) {
      const messageToSend = compact([formInfo.message, customInputMessage]).join('\n\n');
      submitQuery.mutate({ ...formInfo, message: messageToSend });
    }
  }

  useEffect(() => {
    if (!isEqual(initialState, formInfo) && !interacted) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      pushGtmEvent({ event: props.interactionEvent as any });
      setInteracted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formInfo, interacted]);

  function checkValues(formValues: LeadFormRequest) {
    const { firstName, lastName, email, company, phone, webinar_registrant_interest } = formValues;
    if (
      firstName === '' ||
      lastName === '' ||
      email === '' ||
      company === '' ||
      (requirePhoneNumber && phone === '') ||
      (showOrganizationTypeField && !webinar_registrant_interest) ||
      isFreeEmailDomain(email) ||
      isTrashEmailDomain(email)
    ) {
      return false;
    }
    return true;
  }

  if (submitted) {
    return (
      <ModalContentInner>
        <AfterSubmit title={props.successTitle} message={props.successMessage} />
      </ModalContentInner>
    );
  }

  const inputIncomplete = !checkValues(formInfo);

  const Content = inModal ? FormModalContent : NonModalContent;
  const ContentInner = inModal ? ModalContentInner : NonModalContentInner;
  const BottomBar = inModal ? FormModalBottomBar : NonModalBottomBar;
  const Fields = inModal ? FieldsModal : FieldsNonModal;
  return (
    <>
      {props.hideTitle !== true && (
        <FormModalTopBar>
          <Title>{props.title || 'Request a demo with our experts'}</Title>
        </FormModalTopBar>
      )}
      <Content>
        <ContentInner>
          <p style={{ lineHeight: 1.4 }}>
            {props.textBelowTitle ? (
              props.textBelowTitle
            ) : (
              <>Please leave your contact information here, and we will be in touch during the next business day.</>
            )}{' '}
            {props.contactInfo && (
              <>
                Alternatively, contact {props.contactInfo.name} by email (
                <span
                  onClick={() => {
                    if (!emailClicked) {
                      pushGtmEvent({ event: CustomGtmEvent.UA_FORM_EMAIL_ADDRESS_CLICKED });
                      setEmailClicked(true);
                    }
                  }}
                >
                  {props.contactInfo.email}
                </span>
                ) or{' '}
                {phoneRevealed ? (
                  <>phone ({props.contactInfo.phone})</>
                ) : (
                  <span
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={() => {
                      pushGtmEvent({ event: CustomGtmEvent.UA_FORM_PHONE_REVEAL_CLICKED });
                      setPhoneRevealed(true);
                    }}
                  >
                    phone
                  </span>
                )}
                .
              </>
            )}
          </p>
          <Fields>
            <Input
              value={formInfo.firstName}
              onChange={e => {
                setFormInfo({ ...formInfo, firstName: e.target.value });
              }}
              placeholder={'Enter first name'}
              label={'First name*'}
              invalid={!formInfoValidators.firstName(formInfo.firstName)}
              invalidText={'Please provide your first name'}
              validateOnBlur={!submitButtonClicked}
            />
            <Input
              value={formInfo.lastName}
              onChange={e => {
                setFormInfo({ ...formInfo, lastName: e.target.value });
              }}
              placeholder={'Enter last name'}
              label={'Last name*'}
              invalid={!formInfoValidators.lastName(formInfo.lastName)}
              invalidText={'Please provide your last name'}
              validateOnBlur={!submitButtonClicked}
            />
            <Input
              value={formInfo.email}
              onChange={e => {
                setFormInfo({ ...formInfo, email: e.target.value });
              }}
              placeholder={'Enter email'}
              label={'Email*'}
              invalid={!formInfoValidators.email(formInfo.email)}
              invalidText={'Please provide a valid, professional email address.'}
              validateOnBlur={!submitButtonClicked}
              onBlur={() => {
                pushGtmEvent({ event: CustomGtmEvent.UA_FORM_EMAIL_TYPED, data: { email: formInfo.email } });
              }}
            />
            <Input
              value={formInfo.phone}
              onChange={e => {
                setFormInfo({ ...formInfo, phone: e.target.value });
              }}
              placeholder={'Enter phone'}
              label={requirePhoneNumber ? 'Phone*' : 'Phone'}
              invalid={false}
              invalidText={'Please provide a valid phone number'}
              validateOnBlur={!submitButtonClicked}
            />
            <Input
              value={formInfo.company}
              onChange={e => {
                setFormInfo({ ...formInfo, company: e.target.value });
              }}
              placeholder={'Enter company'}
              label={'Company*'}
              invalid={!formInfoValidators.company(formInfo.company)}
              invalidText={'Please provide the name of your company'}
              validateOnBlur={!submitButtonClicked}
            />
            {showOrganizationTypeField && (
              <>
                <StyledLabel>{'What kind of organization do you represent?*'}</StyledLabel>
                <Dropdown
                  placeholder="Please select"
                  selectOptions={[...webinarInterestOptions]}
                  isClearable={true}
                  isMulti={false}
                  selected={undefined}
                  setSelected={(newValue: string | undefined) => {
                    // Note: Field name remains "webinar_registrant_interest" for HubSpot integration compatibility
                    setFormInfo({ ...formInfo, webinar_registrant_interest: newValue });
                  }}
                  styles={{
                    control(styles: unknown) {
                      return {
                        ...(styles && typeof styles === 'object' ? styles : undefined),
                        backgroundColor: 'transparent',
                      };
                    },
                  }}
                />
              </>
            )}
            {props.CustomInputComponent && <props.CustomInputComponent setCustomFieldValue={setCustomInputMessage} />}
            {props.hideMessage !== true ? (
              <StyledTextArea
                value={formInfo.message}
                label={'Message (optional)'}
                placeholder={'Enter optional message…'}
                onChange={e => {
                  setFormInfo({ ...formInfo, message: e.target.value });
                }}
              />
            ) : (
              <></>
            )}
            <Box mt={4} mb={2}>
              We respect your privacy. See our{' '}
              <ExternalLink target="_blank" href="https://uprightproject.com/privacy-policy">
                Privacy Policy
              </ExternalLink>{' '}
              for information regarding the processing of your personal data.
            </Box>
            {props.askConsent && (
              <FormControl sx={{ marginBottom: 2 }}>
                <FormControl.Label sx={{}}>
                  <Box as="div" fontWeight="normal">
                    I agree to receive other communications from The Upright Project
                  </Box>
                </FormControl.Label>
                <Checkbox
                  sx={{ width: '100%' }}
                  checked={formInfo.agreedToComms ? true : false}
                  onChange={_event => {
                    const newValue = !formInfo.agreedToComms;
                    setFormInfo({ ...formInfo, agreedToComms: newValue });
                  }}
                />
              </FormControl>
            )}
            {formInfo.email.endsWith('@uprightproject.com') && (
              <Box mt={3} color="text.blue">
                This form submits to{' '}
                <Link
                  href={`https://app.hubspot.com/submissions/6314920/form/${props.formId}/submissions/`}
                  indicator="underline"
                  target="_blank"
                >
                  this hubspot form
                </Link>
                . This message is only shown when entering an uprightproject.com email address.
              </Box>
            )}
            <Box mt={3} sx={{ color: PlatformColor.TEXT_BLUE }}>
              {inputIncomplete ? (
                <>You may submit once you have filled in all the required fields (*)</>
              ) : (
                <>Thanks for filling in your info! Finish by clicking "submit".</>
              )}
            </Box>
          </Fields>
        </ContentInner>
      </Content>
      <BottomBar>
        <section>
          {/* when in the modal, the styles of the primer submit button are overwritten in
             a weird way, and I don't have time to cleanup that, so we are still using the
             non-primer button when in a modal */}
          {inModal ? (
            <SubmitButton disabled={!checkValues(formInfo)} onClick={submitForm}>
              {props.submitText}
            </SubmitButton>
          ) : (
            <Button size="large" variant="primary" disabled={!checkValues(formInfo)} onClick={submitForm}>
              {props.submitText}
            </Button>
          )}
        </section>
        {submitQuery.error && (
          <ErrorDiv>
            <p>Unexpected: failed to submit request. Please email us at hello@uprightproject.com to book a demo.</p>
            <p>
              Error message: {submitQuery.error.message}, (HTTP status {submitQuery.error.status || 'unknown'})
            </p>
          </ErrorDiv>
        )}
      </BottomBar>
    </>
  );
};

const formInfoValidators: {
  [key in keyof LeadFormRequest]: (value: LeadFormRequest[key] | undefined) => boolean;
} = {
  firstName: value => !!value,
  lastName: value => !!value,
  email: value => !!value && validate(value) && !isFreeEmailDomain(value) && !isTrashEmailDomain(value),
  company: value => !!value,
  webinar_registrant_interest: value => !!value,
  phone: _value => true,
};

const ModalContentInner = styled.div`
  margin: 2em 0;
  margin-bottom: 9em;
`;

const NonModalContentInner = styled.div``;
const NonModalContent = styled.div``;
const NonModalBottomBar = styled.div``;

const Title = styled.h1`
  ${getTitleStyle({ size: 'h5' })}
`;

const FieldsModal = styled.div`
  margin: 2em 0;
  ${tabletOrWider(css`
    margin-bottom: 4em;
  `)}
`;

const FieldsNonModal = styled.div`
  margin: 1em 0;
`;

const Input = styled(BouTextInputFacade)`
  margin: 0.5em 0;
`;

const StyledTextArea = styled(BouTextAreaInput)`
  height: auto;
`;

const ErrorDiv = styled.div`
  ${getTextStyle({ size: 'xxs' })}
  margin-top: 1.0em;
  line-height: 1.3;
  color: ${BouColor.TOOL_TEXT_ERROR_RED};
  > p + p {
    margin-top: 1em;
  }
  /* In the modal the footer is a right-aligned action row and this message is its left-hand item, so
     it takes the free space (DESIGN.md, "Where the action row goes"). Ordered here rather than
     swapped in the DOM, because the same block is used outside the modal, where the footer is a plain
     block and the message belongs below the button. Both declarations are no-ops there. Not focusable,
     so nothing about the tab order changes. */
  order: -1;
  margin-right: auto;
`;

interface AfterSubmitProps {
  title: string;
  message: string;
}

const AfterSubmit = (props: AfterSubmitProps) => (
  <SuccessRoot>
    <SuccessCheckmark color={BouColor.IMPACT_POSITIVE_GREEN} size={'6em'} />
    <h4>{props.title}</h4>
    <p>{props.message}</p>
  </SuccessRoot>
);

const SubmitButton = styled.button`
  ${getNiceButtonStyle({ theme: 'dark' })}
  width: 100%;

  font-size: 130%;

  ${mobileOrWider(css`
    max-width: 250px;
  `)}
`;

const SuccessRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-top: 2em;
  min-height: 280px;

  > * + * {
    margin-top: 1em;
  }

  > h4 {
    ${getTitleStyle({ size: 'h5' })}
  }

  ${mobileOrNarrower(css`
    margin-top: 10vh;
  `)}
`;

const Dropdown = styled(DropdownInput<string>)`
  margin: 0.5em 0;
`;
